import {
  X,
  Users,
  TrendingUp,
  AlertCircle,
  Link,
  ChevronRight,
  Check,
  Info,
  MessageSquare,
  Bell,
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import {
  getCharacterLimit,
  containsEmoji,
  MESSAGE_CHANNELS,
  SMS_CHAR_LIMIT,
  SMS_CHAR_LIMIT_WITH_EMOJI,
  SMS_COST_PER_SEGMENT,
} from '../../../utils/messageUtils';

const QuickSendModal = ({ opportunity, onClose, onSend }) => {
  // Get message templates from opportunity data
  const getMessageTemplate = (opportunity, channel) => {
    return channel === MESSAGE_CHANNELS.SMS
      ? opportunity.messages.sms
      : opportunity.messages.push;
  };

  const [selectedChannel, setSelectedChannel] = useState(
    opportunity?.recommendedChannel || MESSAGE_CHANNELS.SMS
  );
  const [messageText, setMessageText] = useState(
    getMessageTemplate(
      opportunity,
      opportunity?.recommendedChannel || MESSAGE_CHANNELS.SMS
    )
  );
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedOpportunity, setSelectedOpportunity] = useState(opportunity);
  const [characterCount, setCharacterCount] = useState(
    getMessageTemplate(
      opportunity,
      opportunity?.recommendedChannel || MESSAGE_CHANNELS.SMS
    )?.length || 0
  );
  const [showCostInfo, setShowCostInfo] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasCustomEdits, setHasCustomEdits] = useState(false);
  const [showSwitchWarning, setShowSwitchWarning] = useState(false);
  const [pendingChannel, setPendingChannel] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Handle opportunity changes (when switching between different message opportunities)
  useEffect(() => {
    if (opportunity) {
      setSelectedOpportunity(opportunity);
      // Reset to recommended channel or SMS as default
      const recommendedChannel =
        opportunity.recommendedChannel || MESSAGE_CHANNELS.SMS;
      setSelectedChannel(recommendedChannel);
      // Use the message template from the opportunity
      const template = getMessageTemplate(opportunity, recommendedChannel);
      setMessageText(template);
      setCharacterCount(template.length);
      setHasCustomEdits(false); // Reset custom edits when opportunity changes
    }
  }, [opportunity]);

  // Don't render if no opportunity is provided
  if (!opportunity) {
    return null;
  }

  // Get audience count based on selected channel
  const getChannelAudience = (opportunity, channel) => {
    if (!opportunity) return 0;

    if (channel === MESSAGE_CHANNELS.SMS) {
      return opportunity.smsAudience || opportunity.audience || 0;
    } else {
      return (
        opportunity.pushAudience ||
        Math.floor((opportunity.audience || 0) * 0.7)
      ); // Fallback estimate
    }
  };

  const currentAudience = getChannelAudience(
    selectedOpportunity,
    selectedChannel
  );

  // Calculate character limit and other message properties
  const characterLimit = getCharacterLimit(selectedChannel, messageText);

  // Calculate segments and cost
  const messageCount =
    selectedChannel === MESSAGE_CHANNELS.SMS
      ? Math.ceil(
          characterCount /
            (containsEmoji(messageText)
              ? SMS_CHAR_LIMIT_WITH_EMOJI
              : SMS_CHAR_LIMIT)
        )
      : 1;

  const totalCost =
    selectedChannel === MESSAGE_CHANNELS.SMS
      ? currentAudience * messageCount * SMS_COST_PER_SEGMENT
      : 0;

  const costPerRecipient =
    selectedChannel === MESSAGE_CHANNELS.SMS
      ? messageCount * SMS_COST_PER_SEGMENT
      : 0;

  // Parse estimated revenue from opportunity
  const parseRevenue = (revenueStr) => {
    if (!selectedOpportunity) return 0;
    if (typeof revenueStr === 'number') return revenueStr;
    if (typeof revenueStr === 'string') {
      return parseFloat(revenueStr.replace(/[$,]/g, '')) || 0;
    }
    return selectedOpportunity.potentialRevenue || 0;
  };

  const expectedRevenue = parseRevenue(selectedOpportunity?.estimatedRevenue);

  const estimatedROI =
    selectedChannel === MESSAGE_CHANNELS.SMS && totalCost > 0
      ? (expectedRevenue / totalCost).toFixed(1)
      : '∞';

  // Template switching logic
  const isSignificantChange = (original, current) => {
    if (!original || !current) return false;

    // Calculate similarity using simple word comparison
    const originalWords = original.toLowerCase().split(/\s+/);
    const currentWords = current.toLowerCase().split(/\s+/);
    const commonWords = originalWords.filter((word) =>
      currentWords.includes(word)
    );
    const similarity =
      commonWords.length / Math.max(originalWords.length, currentWords.length);

    // Consider it significant if less than 70% similar or length difference > 20%
    const lengthDiff =
      Math.abs(original.length - current.length) / original.length;
    return similarity < 0.7 || lengthDiff > 0.2;
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setMessageText(newText);
    setCharacterCount(newText.length);

    // Check if this is a significant change from the current template
    const currentTemplate = getMessageTemplate(
      selectedOpportunity,
      selectedChannel
    );
    const hasSignificantChanges = isSignificantChange(currentTemplate, newText);
    setHasCustomEdits(hasSignificantChanges);
  };

  const handleChannelChange = (channel) => {
    // If user has custom edits, show warning first
    if (hasCustomEdits && channel !== selectedChannel) {
      setPendingChannel(channel);
      setShowSwitchWarning(true);
      return;
    }

    // Proceed with channel change
    performChannelSwitch(channel);
  };

  const performChannelSwitch = (channel) => {
    setIsTransitioning(true);

    // Start fade out
    setTimeout(() => {
      setSelectedChannel(channel);
      setMessageText(getMessageTemplate(selectedOpportunity, channel));
      setCharacterCount(
        getMessageTemplate(selectedOpportunity, channel).length
      );
      setHasCustomEdits(false); // Reset custom edits flag

      // Fade back in
      setTimeout(() => {
        setIsTransitioning(false);
      }, 50);
    }, 150);
  };

  const handleKeepEdits = () => {
    // Switch channel but keep the current message text
    setSelectedChannel(pendingChannel);
    setShowSwitchWarning(false);
    setPendingChannel(null);
  };

  const handleDiscardEdits = () => {
    // Switch channel and use the new template
    performChannelSwitch(pendingChannel);
    setShowSwitchWarning(false);
    setPendingChannel(null);
  };

  const handleCancelSwitch = () => {
    // Cancel the channel switch
    setShowSwitchWarning(false);
    setPendingChannel(null);
  };

  const handleSend = async () => {
    setIsLoading(true);
    setSending(true);

    try {
      // Simulate API call with proper loading time
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Call the onSend callback
      await onSend({
        message: messageText,
        channel: selectedChannel,
        audience: currentAudience,
        cost: totalCost,
      });

      // Success - close modal
      onClose();
    } catch (error) {
      console.error('Failed to send message:', error);
      // Handle error state if needed
    } finally {
      setIsLoading(false);
      setSending(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => {
        // Close modal when clicking backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      role="presentation"
    >
      {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
      <div
        style={{
          backgroundColor: '#FFFFFF',
          width: '100%',
          maxWidth: '600px',
          maxHeight: '90vh',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          boxShadow: '0 -4px 6px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Message creation modal"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onClose();
          }
        }}
        tabIndex={0}
      >
        {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        {/* Header */}
        <div
          style={{
            padding: window.innerWidth < 768 ? '16px 20px' : '20px 24px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <h2
              style={{
                fontSize: '20px',
                fontWeight: 600,
                color: '#1F2937',
                margin: 0,
              }}
            >
              Quick Send Message
            </h2>
            <p style={{ fontSize: '14px', color: '#6B7280', marginTop: '4px' }}>
              Step {step} of 2
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              borderRadius: '6px',
              transition: 'background-color 0.2s',
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = '#F3F4F6')
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = 'transparent')
            }
          >
            <X className="w-5 h-5" style={{ color: '#6B7280' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {step === 1 ? (
            <div style={{ padding: window.innerWidth < 768 ? '20px' : '24px' }}>
              {/* Channel Selection */}
              <div
                style={{
                  display: 'flex',
                  gap: window.innerWidth < 768 ? '6px' : '8px',
                  marginBottom: '24px',
                  padding: '4px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                }}
              >
                <button
                  onClick={() => handleChannelChange(MESSAGE_CHANNELS.SMS)}
                  disabled={isLoading || sending}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: window.innerWidth < 768 ? '12px 16px' : '8px 16px',
                    minHeight: window.innerWidth < 768 ? '44px' : 'auto',
                    backgroundColor:
                      selectedChannel === MESSAGE_CHANNELS.SMS
                        ? '#FFFFFF'
                        : 'transparent',
                    border:
                      selectedChannel === MESSAGE_CHANNELS.SMS
                        ? '1px solid #E5E7EB'
                        : '1px solid transparent',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight:
                      selectedChannel === MESSAGE_CHANNELS.SMS ? 600 : 400,
                    color:
                      selectedChannel === MESSAGE_CHANNELS.SMS
                        ? '#1F2937'
                        : '#6B7280',
                    cursor: isLoading || sending ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease-in-out',
                    boxShadow:
                      selectedChannel === MESSAGE_CHANNELS.SMS
                        ? '0 1px 2px rgba(0, 0, 0, 0.05)'
                        : 'none',
                    transform:
                      selectedChannel === MESSAGE_CHANNELS.SMS
                        ? 'scale(1.02)'
                        : 'scale(1)',
                    opacity: isLoading || sending ? 0.5 : 1,
                  }}
                >
                  <MessageSquare className="w-4 h-4" />
                  SMS
                </button>
                <button
                  onClick={() => handleChannelChange(MESSAGE_CHANNELS.PUSH)}
                  disabled={isLoading || sending}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    padding: window.innerWidth < 768 ? '12px 16px' : '8px 16px',
                    minHeight: window.innerWidth < 768 ? '44px' : 'auto',
                    backgroundColor:
                      selectedChannel === MESSAGE_CHANNELS.PUSH
                        ? '#FFFFFF'
                        : 'transparent',
                    border:
                      selectedChannel === MESSAGE_CHANNELS.PUSH
                        ? '1px solid #E5E7EB'
                        : '1px solid transparent',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight:
                      selectedChannel === MESSAGE_CHANNELS.PUSH ? 600 : 400,
                    color:
                      selectedChannel === MESSAGE_CHANNELS.PUSH
                        ? '#1F2937'
                        : '#6B7280',
                    cursor: isLoading || sending ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease-in-out',
                    boxShadow:
                      selectedChannel === MESSAGE_CHANNELS.PUSH
                        ? '0 1px 2px rgba(0, 0, 0, 0.05)'
                        : 'none',
                    transform:
                      selectedChannel === MESSAGE_CHANNELS.PUSH
                        ? 'scale(1.02)'
                        : 'scale(1)',
                    opacity: isLoading || sending ? 0.5 : 1,
                  }}
                >
                  <Bell className="w-4 h-4" />
                  Push
                </button>
              </div>

              {/* Opportunity Info */}
              <div
                style={{
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px',
                  border: '1px solid #E5E7EB',
                }}
              >
                <h3
                  style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#1F2937',
                    marginBottom: '4px',
                  }}
                >
                  {selectedOpportunity?.title || 'Message Opportunity'}
                </h3>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginTop: '8px',
                  }}
                >
                  <span
                    style={{
                      fontSize: '14px',
                      color: '#6B7280',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Users className="w-4 h-4" />
                    {currentAudience} recipients
                  </span>
                  <span
                    style={{
                      fontSize: '14px',
                      color: '#10B981',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <TrendingUp className="w-4 h-4" />
                    Est.{' '}
                    {typeof selectedOpportunity?.estimatedRevenue === 'string'
                      ? selectedOpportunity.estimatedRevenue
                      : `$${expectedRevenue.toLocaleString()}`}
                  </span>
                </div>
              </div>

              {/* Audience Difference Indicator */}
              {selectedChannel === MESSAGE_CHANNELS.PUSH &&
                selectedOpportunity.smsAudience &&
                selectedOpportunity.pushAudience &&
                selectedOpportunity.pushAudience <
                  selectedOpportunity.smsAudience && (
                  <div
                    style={{
                      backgroundColor: '#FEF3C7',
                      borderRadius: '6px',
                      padding: '12px',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '8px',
                      border: '1px solid #F59E0B',
                      transition: 'opacity 0.3s ease-in-out',
                      opacity: 1,
                    }}
                  >
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        backgroundColor: '#F59E0B',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        marginTop: '2px',
                      }}
                    >
                      <span
                        style={{
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: 'bold',
                        }}
                      >
                        i
                      </span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          fontSize: '12px',
                          color: '#92400E',
                          margin: '0 0 4px 0',
                          fontWeight: 600,
                        }}
                      >
                        Smaller Push Audience
                      </p>
                      <p
                        style={{
                          fontSize: '12px',
                          color: '#92400E',
                          margin: 0,
                          lineHeight: 1.4,
                        }}
                      >
                        Push reaches{' '}
                        {selectedOpportunity.pushAudience.toLocaleString()}{' '}
                        people vs{' '}
                        {selectedOpportunity.smsAudience.toLocaleString()} for
                        SMS. Limited to app installs.
                      </p>
                    </div>
                  </div>
                )}

              {/* Message Composition */}
              <div style={{ marginBottom: '24px' }}>
                <label
                  htmlFor="message-text"
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6B7280',
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                  }}
                >
                  Message Text
                </label>
                <textarea
                  id="message-text"
                  value={messageText}
                  onChange={handleTextChange}
                  disabled={isLoading || sending}
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '12px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    resize: 'vertical',
                    fontFamily:
                      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    transition: 'opacity 0.3s ease-in-out',
                    opacity: isTransitioning
                      ? 0.5
                      : isLoading || sending
                      ? 0.6
                      : 1,
                    cursor: isLoading || sending ? 'not-allowed' : 'text',
                    backgroundColor:
                      isLoading || sending ? '#F9FAFB' : '#FFFFFF',
                  }}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#6B7280',
                  }}
                >
                  <span>
                    {selectedChannel === MESSAGE_CHANNELS.PUSH
                      ? `${characterCount}/${characterLimit} characters`
                      : `${characterCount} characters`}
                  </span>
                  {selectedChannel === MESSAGE_CHANNELS.SMS && (
                    <span>
                      {messageCount} message{messageCount > 1 ? 's' : ''} per
                      recipient
                    </span>
                  )}
                </div>
              </div>

              {/* Tracking Info - Different for each channel */}
              {selectedChannel === MESSAGE_CHANNELS.SMS ? (
                <div
                  style={{
                    backgroundColor: '#DBEAFE',
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}
                >
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link
                    className="w-3 h-3 flex-shrink-0 mt-1"
                    style={{ color: '#3B82F6' }}
                  />
                  <div style={{ flex: 1 }}>
                    <p
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#1E40AF',
                        marginBottom: '4px',
                      }}
                    >
                      TRACKING ENABLED
                    </p>
                    <p style={{ fontSize: '12px', color: '#1E40AF' }}>
                      Custom deeplinks automatically track clicks and revenue
                      attribution
                    </p>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    backgroundColor: '#DBEAFE',
                    borderRadius: '6px',
                    padding: '12px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}
                >
                  <TrendingUp
                    className="w-4 h-4 flex-shrink-0 mt-1"
                    style={{ color: '#3B82F6' }}
                  />
                  <div style={{ flex: 1 }}>
                    <p
                      style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#1E40AF',
                        marginBottom: '4px',
                      }}
                    >
                      PUSH TRACKING
                    </p>
                    <p style={{ fontSize: '12px', color: '#1E40AF' }}>
                      App opens and ticket purchases are automatically tracked
                    </p>
                  </div>
                </div>
              )}

              {/* Cost Preview for SMS or Free Indicator for Push */}
              {selectedChannel === MESSAGE_CHANNELS.SMS ? (
                <div
                  style={{
                    backgroundColor: '#F9FAFB',
                    borderRadius: '6px',
                    padding: '16px',
                    border: '1px solid #E5E7EB',
                    marginBottom: '24px',
                    transition: 'opacity 0.3s ease-in-out',
                    opacity: isTransitioning ? 0.3 : 1,
                  }}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#1F2937',
                        }}
                      >
                        Message cost
                      </span>
                      <button
                        onClick={() => setShowCostInfo(!showCostInfo)}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          padding: '2px',
                          cursor: 'pointer',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = '#E5E7EB')
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            'transparent')
                        }
                      >
                        <Info
                          className="w-3.5 h-3.5"
                          style={{ color: '#6B7280' }}
                        />
                      </button>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px',
                      }}
                    >
                      <span style={{ fontSize: '14px', color: '#6B7280' }}>
                        Message segments:
                      </span>
                      <span
                        style={{
                          fontSize: '14px',
                          color: '#1F2937',
                          fontWeight: 600,
                        }}
                      >
                        {messageCount}{' '}
                        {messageCount > 1
                          ? `(${characterCount} chars)`
                          : `(${characterCount}/${
                              containsEmoji(messageText)
                                ? SMS_CHAR_LIMIT_WITH_EMOJI
                                : SMS_CHAR_LIMIT
                            } chars)`}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px',
                      }}
                    >
                      <span style={{ fontSize: '14px', color: '#6B7280' }}>
                        Cost per recipient:
                      </span>
                      <span style={{ fontSize: '14px', color: '#1F2937' }}>
                        ${costPerRecipient.toFixed(2)}
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>
                          {' '}
                          ({messageCount} × $0.02)
                        </span>
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px',
                      }}
                    >
                      <span style={{ fontSize: '14px', color: '#6B7280' }}>
                        Recipients:
                      </span>
                      <span style={{ fontSize: '14px', color: '#1F2937' }}>
                        {currentAudience.toLocaleString()}
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingTop: '12px',
                        borderTop: '1px solid #E5E7EB',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#1F2937',
                        }}
                      >
                        Total cost:
                      </span>
                      <span
                        style={{
                          fontSize: '16px',
                          color: '#1F2937',
                          fontWeight: 600,
                        }}
                      >
                        ${totalCost.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {messageCount > 1 && (
                    <div
                      style={{
                        backgroundColor: '#FEF3C7',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        marginTop: '12px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                      }}
                    >
                      <AlertCircle
                        className="w-4 h-4 flex-shrink-0 mt-0.5"
                        style={{ color: '#D97706' }}
                      />
                      <p
                        style={{
                          fontSize: '12px',
                          color: '#92400E',
                          margin: 0,
                        }}
                      >
                        Message exceeds{' '}
                        {containsEmoji(messageText)
                          ? SMS_CHAR_LIMIT_WITH_EMOJI
                          : SMS_CHAR_LIMIT}{' '}
                        characters and will be sent as {messageCount} segments,
                        increasing cost
                      </p>
                    </div>
                  )}

                  {/* Educational info - expandable */}
                  {showCostInfo && (
                    <div
                      style={{
                        marginTop: '16px',
                        paddingTop: '16px',
                        borderTop: '1px solid #E5E7EB',
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: '#FFFFFF',
                          borderRadius: '4px',
                          padding: '12px',
                          fontSize: '12px',
                          color: '#6B7280',
                          lineHeight: '1.6',
                        }}
                      >
                        <p style={{ marginBottom: '8px' }}>
                          <strong>SMS Pricing Explained:</strong>
                        </p>
                        <ul style={{ margin: '0 0 8px 16px', padding: 0 }}>
                          <li>Standard messages: 160 characters = 1 segment</li>
                          <li>
                            Messages with emojis: 70 characters = 1 segment
                          </li>
                          <li>Each segment costs $0.02 per recipient</li>
                        </ul>
                        {containsEmoji(messageText) ? (
                          <div
                            style={{
                              backgroundColor: '#FEF3C7',
                              borderRadius: '4px',
                              padding: '8px',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '6px',
                            }}
                          >
                            <AlertCircle
                              className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                              style={{ color: '#D97706' }}
                            />
                            <p
                              style={{
                                margin: 0,
                                fontSize: '11px',
                                color: '#92400E',
                              }}
                            >
                              Your message contains emojis, reducing the segment
                              size to 70 characters. Consider removing emojis to
                              fit more text per segment.
                            </p>
                          </div>
                        ) : (
                          <div
                            style={{
                              backgroundColor: '#D1FAE5',
                              borderRadius: '4px',
                              padding: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                          >
                            <Check
                              className="w-3.5 h-3.5"
                              style={{ color: '#10B981' }}
                            />
                            <p
                              style={{
                                margin: 0,
                                fontSize: '11px',
                                color: '#047857',
                              }}
                            >
                              Using standard characters for maximum efficiency
                              (160 chars/segment)
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    backgroundColor: '#D1FAE5',
                    borderRadius: '6px',
                    padding: '12px',
                    border: '1px solid #A7F3D0',
                    textAlign: 'center',
                    marginBottom: '24px',
                    transition: 'opacity 0.3s ease-in-out',
                    opacity: isTransitioning ? 0.3 : 1,
                  }}
                >
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#047857',
                      marginBottom: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'center',
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#047857"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                    </svg>
                    Push Notifications Are Free
                  </div>
                  <div style={{ fontSize: '12px', color: '#047857' }}>
                    No messaging costs for push notifications
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Step 2: Confirmation
            <div
              style={{
                padding: window.innerWidth < 768 ? '20px' : '24px',
                paddingBottom: '0',
              }}
            >
              <div style={{ marginBottom: '24px' }}>
                <h3
                  style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#1F2937',
                    marginBottom: '24px',
                  }}
                >
                  Confirm & Send
                </h3>
              </div>

              {/* Confirmation Details */}
              <div
                style={{
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '24px',
                  border: '1px solid #E5E7EB',
                }}
              >
                <div style={{ display: 'grid', gap: '16px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingBottom: '16px',
                      borderBottom: '1px solid #E5E7EB',
                    }}
                  >
                    <span style={{ fontSize: '14px', color: '#6B7280' }}>
                      Recipients
                    </span>
                    <span
                      style={{
                        fontSize: '14px',
                        color: '#1F2937',
                        fontWeight: 600,
                      }}
                    >
                      {currentAudience.toLocaleString()}
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingBottom: '16px',
                      borderBottom: '1px solid #E5E7EB',
                    }}
                  >
                    <span style={{ fontSize: '14px', color: '#6B7280' }}>
                      Message cost
                    </span>
                    <span
                      style={{
                        fontSize: '14px',
                        color:
                          selectedChannel === MESSAGE_CHANNELS.SMS
                            ? '#1F2937'
                            : '#047857',
                        fontWeight: 600,
                      }}
                    >
                      {selectedChannel === MESSAGE_CHANNELS.SMS
                        ? `$${totalCost.toFixed(2)}`
                        : 'Free'}
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingBottom: '16px',
                      borderBottom: '1px solid #E5E7EB',
                    }}
                  >
                    <span style={{ fontSize: '14px', color: '#6B7280' }}>
                      Expected revenue
                    </span>
                    <span
                      style={{
                        fontSize: '14px',
                        color: '#10B981',
                        fontWeight: 600,
                      }}
                    >
                      ${expectedRevenue?.toLocaleString() || '0'}
                    </span>
                  </div>

                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <span style={{ fontSize: '14px', color: '#6B7280' }}>
                      Estimated ROI
                    </span>
                    <span
                      style={{
                        backgroundColor: '#D1FAE5',
                        color: '#10B981',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: 600,
                      }}
                    >
                      {selectedChannel === MESSAGE_CHANNELS.SMS
                        ? `${estimatedROI}x return`
                        : 'Free messaging'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Message Preview */}
              <div style={{ marginBottom: '24px' }}>
                <h4
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#6B7280',
                    marginBottom: '12px',
                    textTransform: 'uppercase',
                    transition: 'color 0.2s ease-in-out',
                  }}
                >
                  {selectedChannel === MESSAGE_CHANNELS.PUSH
                    ? 'Push Notification Preview'
                    : 'SMS Message Preview'}
                </h4>
                <div
                  style={{
                    transition: 'opacity 0.3s ease-in-out',
                    opacity: isTransitioning ? 0.3 : 1,
                  }}
                >
                  {selectedChannel === MESSAGE_CHANNELS.PUSH ? (
                    // iOS Push Notification Preview
                    <div
                      style={{
                        backgroundColor: '#F5F7FA',
                        borderRadius: '12px',
                        padding: '16px',
                        maxWidth: window.innerWidth < 768 ? '100%' : '350px',
                        margin: '0 auto',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      {/* iOS Status Bar */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '12px',
                          fontSize: '11px',
                          fontWeight: 600,
                          color: '#000000',
                        }}
                      >
                        <span>9:41 AM</span>
                        {
                          // eslint-disable-next-line jsx-a11y/accessible-emoji
                          <span>••••• 5G 📶 🔋</span>
                        }
                      </div>

                      {/* Push Notification Card */}
                      <div
                        style={{
                          backgroundColor: '#FFFFFF',
                          borderRadius: '12px',
                          padding: '16px',
                          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                          border: '1px solid #E5E7EB',
                        }}
                      >
                        {/* App Header */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '8px',
                          }}
                        >
                          <div
                            style={{
                              width: '20px',
                              height: '20px',
                              backgroundColor: '#FA4616',
                              borderRadius: '4px',
                              marginRight: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px',
                            }}
                          >
                            <span role="img" aria-label="ticket">
                              🎫
                            </span>
                          </div>
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#1F2937',
                              }}
                            >
                              PitPay
                            </div>
                            <div
                              style={{
                                fontSize: '11px',
                                color: '#6B7280',
                              }}
                            >
                              now
                            </div>
                          </div>
                        </div>

                        {/* Notification Content */}
                        <div
                          style={{
                            fontSize: '14px',
                            lineHeight: '1.4',
                            color: '#1F2937',
                            marginBottom: '12px',
                          }}
                        >
                          {messageText || 'Your message will appear here...'}
                        </div>

                        {/* Action Buttons */}
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                          }}
                        >
                          <button
                            style={{
                              flex: 1,
                              backgroundColor: '#F3F4F6',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              fontSize: '12px',
                              fontWeight: 600,
                              color: '#6B7280',
                              cursor: 'pointer',
                            }}
                          >
                            Close
                          </button>
                          <button
                            style={{
                              flex: 1,
                              backgroundColor: '#FA4616',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 12px',
                              fontSize: '12px',
                              fontWeight: 600,
                              color: 'white',
                              cursor: 'pointer',
                            }}
                          >
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // SMS Message Preview
                    <div
                      style={{
                        backgroundColor: '#000000',
                        borderRadius: '16px',
                        padding: '16px',
                        maxWidth: window.innerWidth < 768 ? '100%' : '320px',
                        margin: '0 auto',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      {/* Phone Status Bar */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '16px',
                          fontSize: '11px',
                          color: '#FFFFFF',
                        }}
                      >
                        <span>9:41 AM</span>
                        <span>••••• 5G</span>
                      </div>

                      {/* Messages Header */}
                      <div
                        style={{
                          textAlign: 'center',
                          marginBottom: '16px',
                          paddingBottom: '8px',
                          borderBottom: '1px solid #333333',
                        }}
                      >
                        <p
                          style={{
                            fontSize: '12px',
                            color: '#999999',
                            margin: 0,
                          }}
                        >
                          Messages
                        </p>
                        <p
                          style={{
                            fontSize: '14px',
                            color: '#FFFFFF',
                            marginTop: '4px',
                          }}
                        >
                          +1 (555) 123-4567
                        </p>
                      </div>

                      {/* Message Bubble */}
                      <div
                        style={{
                          backgroundColor: '#F3F3F3',
                          borderRadius: '16px',
                          padding: '12px 16px',
                          marginLeft: 'auto',
                          marginRight: '8px',
                          maxWidth: '85%',
                          position: 'relative',
                        }}
                      >
                        <p
                          style={{
                            fontSize: '15px',
                            lineHeight: '1.4',
                            color: '#000000',
                            margin: 0,
                            wordWrap: 'break-word',
                          }}
                        >
                          {/* Parse the message to style links and times */}
                          {messageText.split(' ').map((word, index) => {
                            // Check if it's a link
                            if (
                              word.startsWith('http://') ||
                              word.startsWith('https://')
                            ) {
                              return (
                                <span key={index}>
                                  <span
                                    style={{
                                      color: '#007AFF',
                                      textDecoration: 'underline',
                                    }}
                                  >
                                    {word}
                                  </span>{' '}
                                </span>
                              );
                            }
                            // Check if it's a time (simple pattern for PM/AM)
                            else if (word.match(/\d{1,2}(:\d{2})?(AM|PM)/i)) {
                              return (
                                <span key={index}>
                                  <span style={{ textDecoration: 'underline' }}>
                                    {word}
                                  </span>{' '}
                                </span>
                              );
                            }
                            // Regular word
                            else {
                              return <span key={index}>{word} </span>;
                            }
                          })}
                        </p>
                        {/* Message tail */}
                        <div
                          style={{
                            position: 'absolute',
                            bottom: '0',
                            right: '-6px',
                            width: '0',
                            height: '0',
                            borderStyle: 'solid',
                            borderWidth: '0 0 12px 8px',
                            borderColor:
                              'transparent transparent #F3F3F3 transparent',
                          }}
                        ></div>
                      </div>

                      {/* Time sent */}
                      <p
                        style={{
                          textAlign: 'right',
                          fontSize: '11px',
                          color: '#999999',
                          marginTop: '4px',
                          marginRight: '12px',
                        }}
                      >
                        Now
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Success Note */}
              <div
                style={{
                  backgroundColor: '#D1FAE5',
                  borderRadius: '6px',
                  padding: '12px',
                  marginTop: '24px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                }}
              >
                <Check
                  className="w-4 h-4 flex-shrink-0 mt-1"
                  style={{ color: '#10B981' }}
                />
                <p style={{ fontSize: '12px', color: '#047857' }}>
                  {selectedChannel === MESSAGE_CHANNELS.SMS
                    ? "Messages will be sent immediately. You'll see revenue attribution within 8 hours."
                    : 'Push notifications will be sent immediately. Track app opens in your dashboard.'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div
          style={{
            padding: window.innerWidth < 768 ? '16px 20px' : '20px 24px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            gap: '12px',
          }}
        >
          {step === 1 ? (
            <>
              <button
                onClick={onClose}
                style={{
                  flex: 1,
                  backgroundColor: '#FFFFFF',
                  color: '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  padding: window.innerWidth < 768 ? '14px 12px' : '12px',
                  minHeight: window.innerWidth < 768 ? '48px' : 'auto',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F9FAFB';
                  e.currentTarget.style.borderColor = '#D1D5DB';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#FFFFFF';
                  e.currentTarget.style.borderColor = '#E5E7EB';
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                style={{
                  flex: 1,
                  backgroundColor: '#FA4616',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: window.innerWidth < 768 ? '14px 12px' : '12px',
                  minHeight: window.innerWidth < 768 ? '48px' : 'auto',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = '#E03E14')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = '#FA4616')
                }
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep(1)}
                disabled={sending}
                style={{
                  flex: 1,
                  backgroundColor: '#FFFFFF',
                  color: '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  padding: window.innerWidth < 768 ? '14px 12px' : '12px',
                  minHeight: window.innerWidth < 768 ? '48px' : 'auto',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: sending ? 'not-allowed' : 'pointer',
                  opacity: sending ? 0.5 : 1,
                  transition: 'all 0.2s',
                }}
              >
                Back
              </button>
              <button
                onClick={handleSend}
                disabled={sending || isLoading}
                style={{
                  flex: 1,
                  backgroundColor: '#FA4616',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: window.innerWidth < 768 ? '14px 12px' : '12px',
                  minHeight: window.innerWidth < 768 ? '48px' : 'auto',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: sending || isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'background-color 0.2s',
                  opacity: sending || isLoading ? 0.8 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!sending && !isLoading) {
                    e.currentTarget.style.backgroundColor = '#E03E14';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!sending && !isLoading) {
                    e.currentTarget.style.backgroundColor = '#FA4616';
                  }
                }}
              >
                {sending || isLoading ? (
                  <>
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }}
                    />
                    <style>
                      {`
                        @keyframes spin {
                          0% { transform: rotate(0deg); }
                          100% { transform: rotate(360deg); }
                        }
                      `}
                    </style>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="22" y1="2" x2="11" y2="13"></line>
                      <polygon points="22,2 15,22 11,13 2,9"></polygon>
                    </svg>
                    Send Message
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Template Switch Warning Modal */}
      {showSwitchWarning && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
          }}
        >
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '480px',
              margin: '20px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
          >
            <h3
              style={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#1F2937',
                marginBottom: '16px',
                margin: 0,
                fontFamily:
                  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}
            >
              Keep Your Custom Message?
            </h3>
            <p
              style={{
                fontSize: '14px',
                color: '#6B7280',
                marginBottom: '24px',
                marginTop: '24px',
                lineHeight: 1.6,
                fontFamily:
                  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}
            >
              You've made changes to the message template. Would you like to
              keep your custom text when switching to{' '}
              <span style={{ fontWeight: 600, color: '#1F2937' }}>
                {pendingChannel === MESSAGE_CHANNELS.SMS ? 'SMS' : 'Push'}
              </span>
              , or use the default template for that channel?
            </p>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <button
                onClick={handleCancelSwitch}
                style={{
                  backgroundColor: '#FFFFFF',
                  color: '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  width: '100%',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F9FAFB';
                  e.currentTarget.style.borderColor = '#D1D5DB';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#FFFFFF';
                  e.currentTarget.style.borderColor = '#E5E7EB';
                }}
              >
                CANCEL
              </button>
              <button
                onClick={handleDiscardEdits}
                style={{
                  backgroundColor: '#FFFFFF',
                  color: '#FA4616',
                  border: '2px solid #FA4616',
                  borderRadius: '6px',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  width: '100%',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#FEF2F2';
                  e.currentTarget.style.borderColor = '#E03E14';
                  e.currentTarget.style.color = '#E03E14';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#FFFFFF';
                  e.currentTarget.style.borderColor = '#FA4616';
                  e.currentTarget.style.color = '#FA4616';
                }}
              >
                USE TEMPLATE
              </button>
              <button
                onClick={handleKeepEdits}
                style={{
                  backgroundColor: '#FA4616',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  width: '100%',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#E03E14';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#FA4616';
                }}
              >
                KEEP MY TEXT
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main component that includes both button and modal
const MessageCreationTool = ({ opportunity, onClose, onSend }) => {
  // Don't render if no opportunity is provided
  if (!opportunity) {
    return null;
  }

  // Render the modal directly since parent manages visibility
  return (
    <QuickSendModal
      opportunity={opportunity}
      onClose={onClose}
      onSend={onSend}
    />
  );
};

export default MessageCreationTool;
